import { useState } from "react";

interface PhotoPickerProps {
  images: string[];
  onChange: (next: string[]) => void;
}

const SAMPLE_PHOTOS = [
  "https://readdy.ai/api/search-image?query=Professional%20automotive%20photograph%20of%20a%20clean%20silver%20Toyota%20SUV%20parked%20on%20a%20smooth%20dark%20asphalt%20driveway%20with%20soft%20warm%20evening%20light%2C%20seamless%20dark%20grey%20studio-like%20background%2C%20glossy%20paint%20reflections%2C%20premium%20editorial%20car%20rental%20listing%20photo%2C%20high%20detail&width=1200&height=800&seq=gari-host-sample-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Professional%20automotive%20photograph%20of%20a%20deep%20blue%20sedan%20parked%20in%20an%20empty%20concrete%20parking%20garage%20with%20warm%20overhead%20spotlights%2C%20clean%20minimal%20dark%20background%2C%20glossy%20reflective%20paint%2C%20premium%20editorial%20car%20rental%20listing%20photo%2C%20high%20detail&width=1200&height=800&seq=gari-host-sample-02&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Professional%20automotive%20photograph%20of%20a%20white%20compact%20hatchback%20car%20parked%20on%20a%20smooth%20dark%20driveway%20at%20golden%20hour%2C%20softly%20blurred%20warm%20background%2C%20clean%20and%20minimal%2C%20premium%20editorial%20car%20rental%20listing%20photo%2C%20high%20detail&width=1200&height=800&seq=gari-host-sample-03&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Professional%20automotive%20photograph%20of%20a%20black%20luxury%20SUV%20parked%20on%20a%20rooftop%20parking%20deck%20at%20dusk%20with%20warm%20city%20bokeh%20lights%20behind%2C%20moody%20dark%20tone%2C%20glossy%20paint%2C%20premium%20editorial%20car%20rental%20listing%20photo%2C%20high%20detail&width=1200&height=800&seq=gari-host-sample-04&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Professional%20automotive%20photograph%20of%20a%20red%20station%20wagon%20car%20parked%20on%20a%20smooth%20concrete%20floor%20with%20soft%20diffused%20studio%20lighting%2C%20seamless%20charcoal%20grey%20background%2C%20clean%20reflections%2C%20premium%20editorial%20car%20rental%20listing%20photo%2C%20high%20detail&width=1200&height=800&seq=gari-host-sample-05&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Professional%20automotive%20photograph%20of%20a%20grey%20seven%20seater%20van%20parked%20on%20a%20quiet%20tarmac%20road%20at%20soft%20morning%20light%2C%20minimal%20warm%20background%2C%20clean%20glossy%20paint%2C%20premium%20editorial%20car%20rental%20listing%20photo%2C%20high%20detail&width=1200&height=800&seq=gari-host-sample-06&orientation=landscape",
];

export default function PhotoPicker({ images, onChange }: PhotoPickerProps) {
  const [url, setUrl] = useState("");

  const addUrl = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || images.includes(trimmed) || images.length >= 6) return;
    onChange([...images, trimmed]);
  };

  const remove = (value: string) => onChange(images.filter((item) => item !== value));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-foreground-600">
          Add up to 6 photos. Pick a sample below or paste your own image link.
        </p>
        <span className="text-xs text-foreground-500">{images.length}/6 added</span>
      </div>

      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {images.map((image, index) => (
            <div key={image} className="group relative h-24 overflow-hidden rounded-xl border border-background-200 bg-background-100">
              <img src={image} alt={`Car photo ${index + 1}`} title="Selected car photo" className="h-full w-full object-cover object-top" />
              {index === 0 && (
                <span className="absolute left-2 top-2 rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-medium text-background-50">
                  Cover
                </span>
              )}
              <button
                type="button"
                onClick={() => remove(image)}
                aria-label="Remove photo"
                className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-background-50/85 text-foreground-900 backdrop-blur transition-colors hover:bg-secondary-500 hover:text-background-50"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <i className="ri-link pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500"></i>
          <input
            type="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://example.com/my-car.jpg"
            className="w-full rounded-lg border border-background-200/70 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            addUrl(url);
            setUrl("");
          }}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
        >
          <i className="ri-add-line"></i> Add link
        </button>
      </div>

      <p className="mt-5 text-xs font-medium uppercase tracking-wider text-foreground-500">Quick picks</p>
      <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-6">
        {SAMPLE_PHOTOS.map((photo, index) => {
          const selected = images.includes(photo);
          return (
            <button
              key={photo}
              type="button"
              onClick={() => (selected ? remove(photo) : addUrl(photo))}
              aria-label={`Sample car photo ${index + 1}`}
              className={`relative h-16 overflow-hidden rounded-lg border transition-all ${
                selected ? "border-primary-500 ring-2 ring-primary-500/40" : "border-background-200 hover:border-primary-500/60"
              }`}
            >
              <img src={photo} alt={`Sample car photo ${index + 1}`} title="Sample car photo" className="h-full w-full object-cover object-top" />
              {selected && (
                <span className="absolute inset-0 flex items-center justify-center bg-background-50/40 text-lg text-primary-500">
                  <i className="ri-checkbox-circle-fill"></i>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}